import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, FileCheck, Calculator, PieChart, BookOpen, BarChart3, Briefcase, TrendingUp, ArrowRight, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

const Services = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { value: 'all', label: 'All Services' },
    { value: 'financial', label: 'Financial Services' },
    { value: 'tax', label: 'Tax Services' },
    { value: 'compliance', label: 'Compliance & Audit' },
    { value: 'registration', label: 'Registration & Setup' },
  ];

  const services = [
    {
      id: 'accounting-bookkeeping',
      icon: BookOpen,
      title: 'Accounting and Book-keeping',
      category: 'financial',
      description: 'Professional accounting services maintaining accurate financial records and providing valuable business insights.',
      features: [
        'Day-to-day Bookkeeping',
        'Financial Statement Preparation',
        'Accounts Payable/Receivable Management',
        'Bank Reconciliation',
        'Financial Reporting',
      ],
      benefits: [
        'Accurate financial records',
        'Real-time financial visibility',
        'Professional financial statements',
        'Time-saving for business owners',
      ],
    },
    {
      id: 'tax-investment-advisory',
      icon: TrendingUp,
      title: 'Tax Investment and Financial Advisory',
      category: 'financial',
      description: 'Expert guidance on tax-saving investments and comprehensive financial planning for wealth creation.',
      features: [
        'Investment Planning',
        'Tax-Saving Investment Advisory',
        'Retirement Planning',
        'Wealth Management',
        'Portfolio Optimization',
        'Financial Goal Planning',
      ],
      benefits: [
        'Maximize returns while saving tax',
        'Customized investment strategies',
        'Long-term wealth creation',
        'Expert financial guidance',
      ],
    },
    {
      id: 'tax-advisory',
      icon: Calculator,
      title: 'Tax Advisory',
      category: 'tax',
      description: 'Strategic tax planning and advisory services to optimize tax liabilities and ensure compliance with tax laws.',
      features: [
        'Income Tax Planning and Filing',
        'Corporate Tax Advisory',
        'Tax Optimization Strategies',
        'TDS Compliance and Returns',
        'NRI Taxation',
      ],
      benefits: [
        'Minimize tax liabilities legally',
        'Avoid tax penalties and notices',
        'Strategic tax planning',
        'Expert representation before authorities',
      ],
    },
    {
      id: 'gst-advisory',
      icon: PieChart,
      title: 'GST Advisory Services',
      category: 'tax',
      description: 'Comprehensive GST registration, compliance, and advisory for seamless indirect tax management.',
      features: [
        'GST Registration',
        'Monthly/Quarterly GST Returns',
        'GST Audit and Reconciliation',
        'Input Tax Credit Optimization',
        'GST Refund Processing',
        'GST Notice Handling',
      ],
      benefits: [
        'Complete GST compliance',
        'Maximize input tax credit benefits',
        'Reduce GST-related risks',
        'Professional notice management',
      ],
    },
    {
      id: 'company-llp-compliances',
      icon: FileCheck,
      title: 'Company and LLP Compliances',
      category: 'compliance',
      description: 'Ensure complete regulatory compliance with timely filing and adherence to all statutory requirements.',
      features: [
        'Annual ROC Filings',
        'Maintenance of Statutory Registers',
        'Board Meetings and AGM Compliance',
        'Director KYC and Updates',
        'Form Filing and Documentation',
        'Compliance Calendar Management',
      ],
      benefits: [
        'Avoid penalties and legal issues',
        'Timely compliance notifications',
        'Professional document management',
        'Peace of mind with expert handling',
      ],
    },
    {
      id: 'auditing-assurance',
      icon: BarChart3,
      title: 'Auditing and Assurance',
      category: 'compliance',
      description: 'Thorough audit services providing credibility and confidence to stakeholders with detailed financial examination.',
      features: [
        'Statutory Audit',
        'Internal Audit',
        'Tax Audit',
        'Stock Audit',
      ],
      benefits: [
        'Enhanced financial credibility',
        'Risk identification and mitigation',
        'Compliance with audit requirements',
        'Detailed audit reports and insights',
      ],
    },
    {
      id: 'company-registration',
      icon: Building2,
      title: 'Company Registration',
      category: 'registration',
      description: 'Complete assistance in company and LLP registration with expert guidance throughout the incorporation process.',
      features: [
        'Private Limited Company Registration',
        'Name Approval and Documentation',
        'Director Identification Number (DIN)',
        'Digital Signature Certificate (DSC)',
        'One Person Company (OPC) Registration',
        'Limited Liability Partnership (LLP) Formation',
      ],
      benefits: [
        'Quick and hassle-free registration process',
        'Complete legal compliance',
        'Expert guidance on business structure',
        'Post-incorporation support',
      ],
    },
    {
      id: 'business-setup',
      icon: Briefcase,
      title: 'Business Setup',
      category: 'registration',
      description: 'Comprehensive support for establishing and structuring your business for optimal growth and compliance.',
      features: [
        'Business Structure Consultation',
        'Legal Entity Formation',
        'Licensing and Permits',
        'Business Plan Development',
        'Regulatory Compliance Setup',
        'Partnership Deed Drafting',
      ],
      benefits: [
        'Strategic business structuring',
        'Cost-effective setup solutions',
        'Regulatory compliance from day one',
        'Long-term growth planning',
      ],
    },
  ];

  // Filter services based on selected category
  const filteredServices = selectedCategory === 'all' 
    ? services 
    : services.filter(service => service.category === selectedCategory);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-r from-blue-700 to-blue-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.pexels.com/photos/8962446/pexels-photo-8962446.jpeg"
            alt="Services"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Our Services</h1>
            <p className="text-xl text-blue-100">
              Comprehensive financial and compliance solutions tailored to your business needs
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          {/* Category Filter Bar */}
          <div className="max-w-7xl mx-auto mb-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-3">Browse Our Services</h2>
              <p className="text-gray-600">Select a category to filter services</p>
            </div>
            
            {/* Filter Pills */}
            <div className="flex flex-wrap gap-3 justify-center mb-6">
              {categories.map((category) => (
                <button
                  key={category.value}
                  onClick={() => setSelectedCategory(category.value)}
                  className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                    selectedCategory === category.value
                      ? 'bg-gradient-to-r from-blue-600 to-teal-500 text-white shadow-lg transform scale-105'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-md'
                  }`}
                >
                  {category.label}
                </button>
              ))}
            </div>
            
            {/* Results count */}
            <div className="text-center">
              <p className="text-sm text-gray-600">
                Showing <span className="font-bold text-blue-700">{filteredServices.length}</span> {filteredServices.length === 1 ? 'service' : 'services'}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-7xl mx-auto">
            {filteredServices.map((service) => (
              <Card 
                key={service.id}
                className="group hover:shadow-2xl transition-all duration-300 border-2 hover:border-blue-300 overflow-hidden flex flex-col h-full"
              >
                <CardContent className="p-0 flex flex-col h-full">
                  {/* Service Header */}
                  <div className="bg-gradient-to-r from-blue-600 to-teal-500 p-6 text-white">
                    <div className="flex items-center gap-4 mb-3">
                      <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm flex-shrink-0">
                        <service.icon className="text-white" size={28} />
                      </div>
                      <h3 className="text-2xl font-bold">
                        {service.title}
                      </h3>
                    </div>
                    <p className="text-blue-50 leading-relaxed">
                      {service.description}
                    </p>
                  </div>

                  {/* Service Content - Flex grow to push button down */}
                  <div className="p-6 flex-grow flex flex-col">
                    {/* What We Offer */}
                    <div className="mb-6 flex-grow">
                      <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <CheckCircle className="text-teal-600" size={20} />
                        What We Offer
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                        {service.features.map((feature, idx) => (
                          <div key={`${service.id}-feature-${idx}`} className="flex items-start gap-2 text-sm">
                            <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={16} />
                            <span className="text-gray-700">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Key Benefits */}
                    <div className="bg-gradient-to-br from-orange-50 to-blue-50 rounded-xl p-4 mb-6">
                      <h4 className="text-sm font-bold text-gray-900 mb-3">Key Benefits:</h4>
                      <div className="space-y-2">
                        {service.benefits.map((benefit, idx) => (
                          <div key={`${service.id}-benefit-${idx}`} className="flex items-start gap-2">
                            <div className="w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                              <CheckCircle className="text-white" size={14} />
                            </div>
                            <span className="text-sm text-gray-800 font-medium">{benefit}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* CTA Button - Will stay at bottom due to flex structure */}
                    <Link to="/booking" className="block mt-auto">
                      <Button className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white py-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 justify-center">
                        Book Free Consultation
                        <ArrowRight className="ml-2" size={18} />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-700 via-teal-600 to-blue-800 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            <h2 className="text-4xl md:text-5xl font-bold">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-blue-100">
              Schedule a free consultation to discuss how we can help your business achieve its financial goals
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/booking">
                <Button className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-6 text-lg rounded-xl shadow-xl transform hover:scale-105 transition-all duration-300">
                  Book Free Consultation
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <a href="mailto:info@cakja.co.in">
                <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg rounded-xl transform hover:scale-105 transition-all duration-300">
                  Email Us
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
